// src/AdminCancelaciones.js
import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import Swal from "sweetalert2";
import { firestore } from "./firebase";
import { enviarMail } from "./apiEmail";

export default function AdminCancelaciones() {
  const [cancelaciones, setCancelaciones] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [registros, setRegistros] = useState([]);

  // Filtros
  const [vista, setVista] = useState("pendientes"); // "pendientes" | "resueltas" | "todas"
  const [q, setQ] = useState("");

  // === Suscripciones ===
  useEffect(() => {
    const unsubC = firestore.collection("cancelaciones").onSnapshot((snap) => {
      const arr = snap.docs.map((d) => {
        const raw = d.data();
        return {
          id: d.id,
          ...raw,
          creadoEn: raw.creadoEn?.toDate ? raw.creadoEn.toDate() : raw.creadoEn || null,
          resueltaEn: raw.resueltaEn?.toDate ? raw.resueltaEn.toDate() : raw.resueltaEn || null,
        };
      });
      setCancelaciones(arr);
    });

    const unsubR = firestore.collection("reservas").onSnapshot((snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setReservas(arr);
    });

    // NUEVO: registros (Camping / Día)
    const unsubG = firestore.collection("registros").onSnapshot((snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRegistros(arr);
    });

    return () => {
      unsubC();
      unsubR();
      unsubG();
    };
  }, []);

  // Helpers para buscar docs
  const rById = (id) => reservas.find((r) => r.id === id);
  const gById = (id) => registros.find((g) => g.id === id);

  // === Aprobar (reserva o registro) ===
  const aprobar = async (sol) => {
    const isReserva = !!sol.reservaId;
    const isRegistro = !!sol.registroId;

    const r = isReserva ? rById(sol.reservaId) : null;
    const g = isRegistro ? gById(sol.registroId) : null;

    const titulo = isReserva ? "Aprobar cancelación" : "Aprobar eliminación de registro";
    const detalle =
      isReserva && r
        ? `
          <p><b>Casa:</b> ${r.casa}</p>
          <p><b>Fechas:</b> ${format(r.fechaInicio.toDate?.() ?? r.fechaInicio, "dd/MM/yyyy")} – ${format(
            r.fechaFin.toDate?.() ?? r.fechaFin,
            "dd/MM/yyyy"
          )}</p>
        `
        : isRegistro && g
        ? `
          <p><b>Tipo:</b> ${g.tipo || sol.registroTipo || "-"}</p>
          ${
            g.tipo === "camping"
              ? `<p><b>Rango:</b> ${format(g.inicio?.toDate?.() ?? g.inicio, "dd/MM/yyyy")} – ${format(
                  g.fin?.toDate?.() ?? g.fin,
                  "dd/MM/yyyy"
                )}</p>
                 <p><b>Personas:</b> ${g.personas ?? "-"}</p>`
              : g.tipo === "dia"
              ? `<p><b>Fecha:</b> ${format(g.fecha?.toDate?.() ?? g.fecha, "dd/MM/yyyy")}</p>
                 <p><b>Personas:</b> ${g.personas ?? "-"}</p>`
              : ""
          }
        `
        : "<p><i>(No se encontró el documento en memoria)</i></p>";

    const confirm = await Swal.fire({
      title: titulo,
      html: `
        <div style="text-align:left">
          <p><b>Usuario:</b> ${((sol.usuarioNombre || "") + " " + (sol.usuarioApellido || "")).trim() || sol.usuarioEmail}</p>
          ${isReserva ? `<p><b>Reserva ID:</b> ${sol.reservaId}</p>` : ""}
          ${isRegistro ? `<p><b>Registro ID:</b> ${sol.registroId}</p>` : ""}
          ${detalle}
          <p><b>Motivo:</b> ${sol.detalleOtro || sol.motivo || "-"}</p>
        </div>
        <p style="margin-top:8px">¿Confirmás eliminar ${
          isReserva ? "la reserva" : "el registro"
        } y notificar al usuario?</p>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, aprobar",
      cancelButtonText: "No",
    });
    if (!confirm.isConfirmed) return;

    try {
      if (isReserva) {
        await firestore.collection("reservas").doc(sol.reservaId).delete().catch(() => {});
      } else if (isRegistro) {
        await firestore.collection("registros").doc(sol.registroId).delete().catch(() => {});
      }

      await firestore.collection("cancelaciones").doc(sol.id).update({
        estado: "aprobada",
        resueltaEn: new Date(),
      });

      // Mail al usuario
      try {
        if (isReserva) {
          const fIni = r?.fechaInicio?.toDate?.() ?? r?.fechaInicio ?? null;
          const fFin = r?.fechaFin?.toDate?.() ?? r?.fechaFin ?? null;
          const rango = fIni && fFin ? `${format(fIni, "dd/MM/yyyy")} – ${format(fFin, "dd/MM/yyyy")}` : null;
          const subject =
            r ? `✅ Cancelación aprobada — ${r.casa}${rango ? ` (${rango})` : ""}` : `✅ Cancelación aprobada — Reserva ${sol.reservaId}`;

          await enviarMail({
            to: sol.usuarioEmail,
            subject,
            html: `
              <p>Hola ${((sol.usuarioNombre || "") + " " + (sol.usuarioApellido || "")).trim() || ""},</p>
              <p>Tu solicitud de cancelación fue <b>aprobada</b>.</p>
              ${r ? `<p>Casa: <b>${r.casa}</b><br/>Fechas: ${rango || "-"}</p>` : ""}
              <p>Saludos,<br/>Reservas Cuchi</p>
            `,
            text:
              `Tu solicitud de cancelación fue aprobada.\n` +
              (r ? `Casa: ${r.casa}\nFechas: ${rango || "-"}\n` : "") +
              `Saludos, Reservas Cuchi`,
          });
        } else {
          // Registro (Camping/Día)
          const etiqueta =
            g?.tipo === "camping"
              ? `Camping ${g.inicio ? format(g.inicio?.toDate?.() ?? g.inicio, "dd/MM/yyyy") : ""} – ${
                  g.fin ? format(g.fin?.toDate?.() ?? g.fin, "dd/MM/yyyy") : ""
                }`
              : g?.tipo === "dia"
              ? `Día ${g.fecha ? format(g.fecha?.toDate?.() ?? g.fecha, "dd/MM/yyyy") : ""}`
              : "Registro";

          await enviarMail({
            to: sol.usuarioEmail,
            subject: `✅ Eliminación aprobada — ${etiqueta}`,
            html: `
              <p>Hola ${((sol.usuarioNombre || "") + " " + (sol.usuarioApellido || "")).trim() || ""},</p>
              <p>Tu solicitud de eliminación del registro fue <b>aprobada</b> y el registro fue eliminado.</p>
              <p>Saludos,<br/>Reservas Cuchi</p>
            `,
            text: `Tu solicitud de eliminación del registro fue aprobada y el registro fue eliminado.\nSaludos, Reservas Cuchi`,
          });
        }
      } catch (e) {
        console.error("No se pudo notificar al usuario:", e);
      }

      Swal.fire("Listo", `Se aprobó la solicitud y se eliminó ${isReserva ? "la reserva" : "el registro"}.`, "success");
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "No se pudo aprobar la solicitud.", "error");
    }
  };

  // === Rechazar (reserva o registro) ===
  const rechazar = async (sol) => {
    const isReserva = !!sol.reservaId;
    const isRegistro = !!sol.registroId;

    const r = isReserva ? rById(sol.reservaId) : null;
    const g = isRegistro ? gById(sol.registroId) : null;

    const confirm = await Swal.fire({
      title: isReserva ? "Rechazar cancelación" : "Rechazar eliminación de registro",
      text: "¿Seguro? Se notificará al usuario.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, rechazar",
      cancelButtonText: "No",
    });
    if (!confirm.isConfirmed) return;

    try {
      // 1) Marcar la solicitud como rechazada
      await firestore.collection("cancelaciones").doc(sol.id).update({
        estado: "rechazada",
        resueltaEn: new Date(),
      });

      // 2) Quitar el flag en la reserva/registro
      try {
        if (isReserva) {
          await firestore.collection("reservas").doc(sol.reservaId).update({
            cancelRequested: false,
            cancelRequestId: "",
          });
        } else if (isRegistro) {
          await firestore.collection("registros").doc(sol.registroId).update({
            cancelRequested: false,
            cancelRequestId: "",
          });
        }
      } catch (_) {}

      // 3) Notificar al usuario
      try {
        if (isReserva) {
          const asunto = r
            ? `Cancelación rechazada · ${r.casa} · ${format(r.fechaInicio.toDate?.() ?? r.fechaInicio, "dd/MM/yyyy")} – ${format(
                r.fechaFin.toDate?.() ?? r.fechaFin,
                "dd/MM/yyyy"
              )}`
            : "Cancelación rechazada";

          await enviarMail({
            to: sol.usuarioEmail,
            subject: asunto,
            html: `
              <p>Hola ${(sol.usuarioNombre || "") + " " + (sol.usuarioApellido || "")},</p>
              <p>Tu solicitud de cancelación fue <b>rechazada</b>.</p>
              ${
                r
                  ? `<p>Reserva: <b>${r.casa}</b><br/>
                      Fechas: ${format(r.fechaInicio.toDate?.() ?? r.fechaInicio, "dd/MM/yyyy")} – ${format(
                        r.fechaFin.toDate?.() ?? r.fechaFin,
                        "dd/MM/yyyy"
                      )}</p>`
                  : ""
              }
              <p>Si necesitás cambiar la fecha, elegí "Quiero cambiar la fecha" al solicitar.</p>
              <p>Saludos,<br/>Reservas Cuchi</p>
            `,
            text:
              `Tu solicitud de cancelación fue rechazada.\n` +
              (r
                ? `Reserva: ${r.casa}\nFechas: ${format(r.fechaInicio.toDate?.() ?? r.fechaInicio, "dd/MM/yyyy")} – ${format(
                    r.fechaFin.toDate?.() ?? r.fechaFin,
                    "dd/MM/yyyy"
                  )}\n`
                : "") +
              `Si necesitás cambiar la fecha, elegí "Quiero cambiar la fecha" al solicitar.\n`,
          });
        } else {
          const etiqueta =
            g?.tipo === "camping"
              ? `Camping ${g.inicio ? format(g.inicio?.toDate?.() ?? g.inicio, "dd/MM/yyyy") : ""} – ${
                  g.fin ? format(g.fin?.toDate?.() ?? g.fin, "dd/MM/yyyy") : ""
                }`
              : g?.tipo === "dia"
              ? `Día ${g.fecha ? format(g.fecha?.toDate?.() ?? g.fecha, "dd/MM/yyyy") : ""}`
              : "Registro";

          await enviarMail({
            to: sol.usuarioEmail,
            subject: `Eliminación rechazada — ${etiqueta}`,
            html: `
              <p>Hola ${(sol.usuarioNombre || "") + " " + (sol.usuarioApellido || "")},</p>
              <p>Tu solicitud de eliminación del registro fue <b>rechazada</b>.</p>
              <p>Si necesitás modificar la fecha o la cantidad de personas, escribinos por WhatsApp.</p>
              <p>Saludos,<br/>Reservas Cuchi</p>
            `,
            text:
              `Tu solicitud de eliminación del registro fue rechazada.\n` +
              `Si necesitás modificar la fecha o la cantidad de personas, escribinos por WhatsApp.\n`,
          });
        }
      } catch (e) {
        console.error("No se pudo notificar al usuario:", e);
      }

      Swal.fire("Listo", "Solicitud rechazada.", "success");
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "No se pudo rechazar la solicitud.", "error");
    }
  };

  // ---- Filtros, conteos y búsqueda ----
  const pendientes = cancelaciones.filter((x) => x.estado === "pendiente").length;
  const resueltas = cancelaciones.length - pendientes;

  const qn = q.trim().toLowerCase();

  const filtradas = cancelaciones
    // filtro por estado
    .filter((sol) => {
      if (vista === "pendientes") return sol.estado === "pendiente";
      if (vista === "resueltas") return sol.estado !== "pendiente";
      return true; // "todas"
    })
    // búsqueda por nombre/email/casa/tipo/id
    .filter((sol) => {
      if (!qn) return true;

      const r = sol.reservaId ? rById(sol.reservaId) : null;
      const g = sol.registroId ? gById(sol.registroId) : null;

      const nombre = `${sol.usuarioNombre || ""} ${sol.usuarioApellido || ""}`.trim().toLowerCase();
      const email = (sol.usuarioEmail || "").toLowerCase();
      const casa = (r?.casa || "").toLowerCase();
      const tipo = (g?.tipo || sol.registroTipo || "").toLowerCase();
      const id = (sol.reservaId || sol.registroId || "").toLowerCase();

      return [nombre, email, casa, tipo, id].some((s) => s.includes(qn));
    })
    // orden por fecha desc
    .sort((a, b) => (b.creadoEn?.getTime?.() || 0) - (a.creadoEn?.getTime?.() || 0));

  return (
    <div>
      <h2>Cancelaciones</h2>

      {/* Toolbar de filtros + búsqueda */}
      <div className="cancel-toolbar">
        <div className="filters">
          <button
            className={vista === "pendientes" ? "pill active" : "pill"}
            onClick={() => setVista("pendientes")}
            type="button"
          >
            Pendientes ({pendientes})
          </button>
          <button
            className={vista === "resueltas" ? "pill active" : "pill"}
            onClick={() => setVista("resueltas")}
            type="button"
          >
            Resueltas ({resueltas})
          </button>
          <button
            className={vista === "todas" ? "pill active" : "pill"}
            onClick={() => setVista("todas")}
            type="button"
          >
            Todas ({cancelaciones.length})
          </button>
        </div>

        <input
          className="cancel-search"
          type="search"
          placeholder="Buscar: nombre, email, casa/tipo o ID…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {filtradas.length === 0 ? (
        <p>No hay solicitudes.</p>
      ) : (
        <div className="cancel-grid">
          {filtradas.map((sol) => {
            const isReserva = !!sol.reservaId;
            const isRegistro = !!sol.registroId;

            const r = isReserva ? rById(sol.reservaId) : null;
            const g = isRegistro ? gById(sol.registroId) : null;

            const nombreMostrar =
              ((sol.usuarioNombre || "") + " " + (sol.usuarioApellido || "")).trim() ||
              r?.nombre ||
              g?.nombre ||
              sol.usuarioEmail;

            // Detalle compacto por tipo
            const detalleReserva =
              r &&
              `${r.casa} — ${format(r.fechaInicio.toDate?.() ?? r.fechaInicio, "dd/MM/yyyy")} – ${format(
                r.fechaFin.toDate?.() ?? r.fechaFin,
                "dd/MM/yyyy"
              )}`;

            let detalleRegistro = "";
            if (g) {
              if (g.tipo === "camping") {
                detalleRegistro = `Camping — ${format(g.inicio?.toDate?.() ?? g.inicio, "dd/MM/yyyy")} – ${format(
                  g.fin?.toDate?.() ?? g.fin,
                  "dd/MM/yyyy"
                )} • ${g.personas ?? "-"} pers`;
              } else if (g.tipo === "dia") {
                detalleRegistro = `Día — ${format(g.fecha?.toDate?.() ?? g.fecha, "dd/MM/yyyy")} • ${g.personas ?? "-"} pers`;
              }
            }

            return (
              <div key={sol.id} className="cancel-card">
                <div className="cancel-main">
                  <div className="row">
                    <span className={`badge ${sol.estado}`}>{sol.estado}</span>
                    <small className="muted">{sol.creadoEn ? format(sol.creadoEn, "dd/MM/yyyy, H:mm") : "-"}</small>
                  </div>

                  <div className="cols">
                    <div>
                      <div className="label">Usuario:</div>
                      <div className="value">{nombreMostrar}</div>
                    </div>
                    <div>
                      <div className="label">{isReserva ? "Reserva ID:" : "Registro ID:"}</div>
                      <div className="value mono">{isReserva ? sol.reservaId : sol.registroId}</div>
                    </div>
                    <div>
                      <div className="label">Motivo:</div>
                      <div className="value">{sol.detalleOtro || sol.motivo || "-"}</div>
                    </div>
                  </div>

                  <div className="cols">
                    <div>
                      <div className="label">Detalle:</div>
                      <div className="value">
                        {isReserva ? detalleReserva || <i>(No encontrada)</i> : detalleRegistro || <i>(No encontrado)</i>}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="actions">
                  {sol.estado === "pendiente" ? (
                    <>
                      <button onClick={() => aprobar(sol)}>Aprobar</button>
                      <button className="secondary" onClick={() => rechazar(sol)}>
                        Rechazar
                      </button>
                    </>
                  ) : (
                    <small className="muted">
                      Resuelta: {sol.resueltaEn ? format(sol.resueltaEn, "dd/MM/yyyy, H:mm") : "-"}
                    </small>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
